import { useState, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { apiFetch } from '../../../api/core';
import { useCRMContext } from '../CRMContext'; // Adjust path if needed
import { useContactsImportFieldsMapping } from './useContactsImportFieldsMapping';

// --- Types & Constants ---

export type ImportState = 'none' | 'upload' | 'map_fields' | 'importing' | 'done';

export interface ImportRowState {
  status: 'pending' | 'success' | 'error';
  errorMsg?: string;
}

export interface ImportParams {
  tags: number[];
  categories?: string[]; // Added categories
  consent_pd?: string;
  consent_emails?: string;
  communication_language?: string[]; 
}

// --- Hook Implementation ---

export const useImportContacts = () => {
  const ctx = useCRMContext();
  const contactsApiPath = `${ctx.apiPath}/contact/import`;

  // Extracted mapping logic
  const { 
    mappableFields, 
    mapping, 
    updateMapping, 
    resetMapping, 
    applyMapping 
  } = useContactsImportFieldsMapping();

  // State Machine
  const [importState, setImportState] = useState<ImportState>('none');
  const [isLoading, setIsLoading] = useState(false);
  
  // Data States
  const [rows, setRows] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [params, setParams] = useState<ImportParams>({ tags: [], categories: [] });
  const [rowStates, setRowStates] = useState<Record<number, ImportRowState>>({});

  const open = useCallback(() => setImportState('upload'), []);
  
  const cancel = useCallback(() => {
    setImportState('none');
    setRows([]);
    setHeaders([]);
    resetMapping(); 
    setParams({ tags: [], categories: [] }); // Reset categories
    setRowStates({});
    setIsLoading(false);
  }, [resetMapping]);

  const isOpen = importState !== 'none';

  const goBack = useCallback(() => {
    switch (importState) {
      case 'upload':
      case 'done':
        cancel();
        break;
      case 'map_fields':
        setImportState('upload');
        break;
      case 'importing':
        setImportState('map_fields');
        break;
      default:
        cancel();
    }
  }, [importState, cancel]);

  // 5. fn to upload an xlsx file (Rewritten with exceljs)
  const uploadFile = useCallback(async (file: File) => {
    setIsLoading(true);
    
    try {
      const buffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      
      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error("В файле нет листов");
      }

      const extractedHeaders: string[] = [];
      const headerColMap: Record<number, string> = {};
      const jsonRows: any[] = [];

      // Extract Headers from row 1
      const firstRow = worksheet.getRow(1);
      firstRow.eachCell((cell, colNumber) => {
        const headerVal = cell.text?.trim() || `Колонка ${colNumber}`;
        headerColMap[colNumber] = headerVal;
        extractedHeaders.push(headerVal);
      });

      // Extract Data from row 2 onwards
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Skip header row

        const rowData: Record<string, any> = {};
        let hasData = false;

        row.eachCell((cell, colNumber) => {
          const headerName = headerColMap[colNumber];
          if (headerName) {
            let cellValue: any = cell.value;
            
            // Normalize ExcelJS complex object types (formulas, rich text, hyperlinks, etc.)
            if (cellValue !== null && typeof cellValue === 'object') {
              if ('result' in cellValue) {
                cellValue = cellValue.result; // Computed formula result
              } else if ('richText' in cellValue) {
                cellValue = (cellValue as any).richText.map((rt: any) => rt.text).join(''); // Rich text
              } else if (cellValue instanceof Date) {
                cellValue = cellValue.toISOString(); // Convert dates to standard strings
              } else if ('text' in cellValue) {
                cellValue = cellValue.text; // Handles Hyperlinks {text: '...', hyperlink: '...'}
              } else if ('hyperlink' in cellValue) {
                cellValue = cellValue.hyperlink; // Fallback if it only has the URL
              }
            }
            
            rowData[headerName] = cellValue;
            hasData = true;
          }
        });

        if (hasData) {
          jsonRows.push(rowData);
        }
      });

      setHeaders(extractedHeaders);
      setRows(jsonRows);
      setImportState('map_fields');
    } catch (error) {
      console.error("Failed to parse Excel file", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateParams = useCallback((newParams: Partial<ImportParams>) => {
    setParams(prev => ({ ...prev, ...newParams }));
  }, []);

  const doImport = useCallback(async () => {
    setImportState('importing');
    setIsLoading(true);
    
    const initialRowStates: Record<number, ImportRowState> = {};
    rows.forEach((_, idx) => {
      initialRowStates[idx] = { status: 'pending' };
    });
    setRowStates(initialRowStates);

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      
      const payload = applyMapping(row);
      
      if (params.tags?.length) payload.tags = params.tags; 
      // Add categories if specified
      if (params.categories?.length) payload.categories = params.categories; 
      if (params.consent_pd) payload.consent_pd = params.consent_pd;
      if (params.consent_emails) payload.consent_emails = params.consent_emails;
      if (params.communication_language && params.communication_language.length > 0) {
        payload.communication_language = params.communication_language;
      }

      try {
         await apiFetch<any>(contactsApiPath, 'post', payload);
         setRowStates(prev => ({ ...prev, [i]: { status: 'success' } }));
      } catch (error: any) {
         const errorMessage = error?.message || error?.data?.message || "Неизвестная ошибка";
         setRowStates(prev => ({ ...prev, [i]: { status: 'error', errorMsg: errorMessage } }));
      }
    }

    setIsLoading(false);
    setImportState('done');
  }, [rows, applyMapping, params, contactsApiPath]);

  const downloadErrors = useCallback(async () => {
    const errorIndices = Object.keys(rowStates)
      .map(Number)
      .filter(idx => rowStates[idx].status === 'error');

    if (errorIndices.length === 0) return;

    setIsLoading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Ошибки импорта');

      const errorHeader = 'Ошибка (System Error Msg)';
      const headerRow = worksheet.addRow([...headers, errorHeader]);

      headerRow.font = { bold: true };
      headerRow.eachCell((cell, colNumber) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: colNumber > headers.length ? 'FFFFCCCC' : 'FFF0F0F0' } 
        };
      });

      errorIndices.forEach(idx => {
        const originalRow = rows[idx];
        const errorMsg = rowStates[idx].errorMsg || 'Неизвестная ошибка';
        
        const rowData = headers.map(h => {
          let val = originalRow[h];
          
          // Defensive object flattening to prevent [object Object] in the final file
          if (val !== null && typeof val === 'object') {
            val = val.text || val.hyperlink || JSON.stringify(val);
          }
          
          return val !== undefined && val !== null ? String(val) : '';
        });
        
        rowData.push(errorMsg);
        worksheet.addRow(rowData);
      });

      worksheet.columns.forEach(column => {
        let maxLength = 10;
        column.eachCell?.({ includeEmpty: true }, cell => {
          const columnLength = cell.value ? cell.value.toString().length : 10;
          if (columnLength > maxLength) maxLength = columnLength + 2;
        });
        column.width = Math.min(maxLength, 50);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `Ошибки_импорта_${dateStr}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to generate error report", error);
    } finally {
      setIsLoading(false);
    }
  }, [rows, headers, rowStates]);

  return {
    importState, isOpen, isLoading,
    rows, headers, rowStates,
    mappableFields, mapping, params,
    open, cancel, goBack, uploadFile,
    updateMapping, updateParams, doImport, downloadErrors,
  };
};

export type ImportContactsData = ReturnType<typeof useImportContacts>;